import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { usePlacementApi } from '@post-office/use-placement-api';

import { useState, useEffect } from 'react';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import type { MessageContextWithFeatureId } from '@post-office/browser--townsquare/common/types';

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type AppProps = {
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
	eligibleMessage?: EligibleMessage<Record<string, never>>;
};

export type PlacementRenderProps = {
	dismiss: VoidFunction;
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
} & EligibleMessage;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'home-app-changeboarding',
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const { eligibleMessage } = appProps;
	const providedMessage =
		FeatureGates.checkGate('townsquare_enable_messages_as_placement_props') && eligibleMessage
			? eligibleMessage
			: undefined;

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>('/', providedMessage ? { autoRefetch: { isEnabled: false, updateKey: '' } } : undefined);
	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>(
		providedMessage ? [providedMessage] : [],
	);

	useEffect(() => {
		if (providedMessage || isLoading || error) {
			return;
		}
		setMessages(data.messages);
	}, [providedMessage, data, error, isLoading]);

	if ((!providedMessage && (error || isLoading)) || messages.length === 0) {
		return null;
	}

	const { featureId, children } = appProps;

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);
			return filteredMessages;
		});
	};

	// if placement was given a specifc message, then render that
	// otherwise render the message which matches featureId given
	const messageToRender = providedMessage
		? messages[0]
		: messages.find((message) => {
				const homeMessage = message as MessageContextWithFeatureId;
				return homeMessage.featureId === featureId;
			});

	const childrenFunction = children || (() => null);
	return (
		<>
			{messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
				>
					{childrenFunction}
				</Messages.render>
			) : null}
		</>
	);
});
